import React, { useEffect, useState } from 'react';
import { SIA_PERSONALIZATION_FAILED_REASON } from '@/utils/constants/sia';
import {
  SIA_PERSONALIZATION_COMPLETED_NOTIF_TYPE,
  SIA_PERSONALIZATION_FAILED_NOTIF_TYPE,
} from '@Saral-Influencer-Marketing/saral-utils/src/constants/notification';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import { fetchNotifications } from '../../../redux/notifications/notificationActions';
import { updateSeenStatus } from '../../../services/notifications.service';
import CustomNotification from '../CustomNotification/CustomNotification';

// Key for localStorage
const LAST_NOTIFICATION_TIME_KEY = 'saral_last_notification_time';

export default function NotificationsProvider({ children }) {
  const showNotificationDefault = {
    postTracking: false,
    siaPersonalizationSuccess: false,
    siaPersonalizationFailed: false,
  };

  const [showNotification, setShowNotification] = useState(showNotificationDefault);
  const [currentNotifications, setCurrentNotifications] = useState([]);
  const [groupedNotifications, setGroupedNotifications] = useState([]);

  const [siaPersonalizationFailedNotification, setSiaPersonalizationFailedNotification] = useState(null);

  const { notifications } = useSelector((state) => state.notifications);

  const dispatch = useDispatch();

  const handleUpdateShowNotification = (type, value) => {
    setShowNotification((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  useEffect(() => {
    // Only process if we're not already showing a notification
    if (showNotification.postTracking || showNotification.siaPersonalizationSuccess) return;

    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

    // Check if 5 minutes have passed since the last notification using localStorage
    const currentTime = now.getTime();
    const lastNotificationTime = parseInt(localStorage.getItem(LAST_NOTIFICATION_TIME_KEY) || '0', 10);
    const timeSinceLastNotification = currentTime - lastNotificationTime;
    const fiveMinutesInMs = 5 * 60 * 1000;

    const unreadSiaSuccessNotifs = notifications.filter(
      (notification) =>
        notification.type === SIA_PERSONALIZATION_COMPLETED_NOTIF_TYPE &&
        !notification.seen &&
        dayjs(notification.createdAt).isAfter(fiveMinutesAgo)
    );

    if (unreadSiaSuccessNotifs.length > 0) {
      handleUpdateShowNotification('siaPersonalizationSuccess', true);
      markNotificationsAsRead(unreadSiaSuccessNotifs);
    }

    const unreadSiaFailedNotif = notifications.find(
      (notification) =>
        notification.type === SIA_PERSONALIZATION_FAILED_NOTIF_TYPE &&
        !notification.seen &&
        new Date(notification.createdAt) > fiveMinutesAgo
    );

    if (unreadSiaFailedNotif) {
      handleUpdateShowNotification('siaPersonalizationFailed', true);
      setSiaPersonalizationFailedNotification(unreadSiaFailedNotif);
      markNotificationsAsRead([unreadSiaFailedNotif]);
    }

    if (timeSinceLastNotification < fiveMinutesInMs) {
      // Less than 5 minutes have passed, don't show a notification
      return;
    }

    // Filter for unseen postTracking notifications
    const unreadPostTrackingNotifications = notifications.filter(
      (notification) =>
        notification.type === 'postTracking' && !notification.seen && new Date(notification.createdAt) > fiveMinutesAgo
    );

    // If we have notifications to show
    if (unreadPostTrackingNotifications.length > 0) {
      // Group notifications by influencer ID
      const grouped = groupNotificationsByInfluencer(unreadPostTrackingNotifications);

      // Update the last notification time in localStorage
      localStorage.setItem(LAST_NOTIFICATION_TIME_KEY, currentTime.toString());

      setCurrentNotifications(unreadPostTrackingNotifications);
      setGroupedNotifications(grouped);
      handleUpdateShowNotification('postTracking', true);

      // Mark notifications as read as soon as they're shown
      markNotificationsAsRead(unreadPostTrackingNotifications);
    }
  }, [
    notifications,
    showNotification.postTracking,
    showNotification.siaPersonalizationSuccess,
    showNotification.siaPersonalizationFailed,
  ]);

  // Group notifications by influencer ID
  const groupNotificationsByInfluencer = (notifications) => {
    const groups = {};

    notifications.forEach((notification) => {
      const influencerId = notification.influencerId?._id;
      if (!influencerId) return;

      if (!groups[influencerId]) {
        groups[influencerId] = {
          influencer: notification.influencerId,
          content: notification.content,
          notifications: [],
        };
      }

      groups[influencerId].notifications.push(notification);
    });

    // Convert to array and sort by number of posts (descending)
    return Object.values(groups).sort((a, b) => b.notifications.length - a.notifications.length);
  };

  // Function to mark notifications as read
  const markNotificationsAsRead = async (notificationsToMark) => {
    const notificationIds = notificationsToMark?.map((notification) => notification._id);

    try {
      await updateSeenStatus({ notificationIds });
      dispatch(fetchNotifications());
    } catch (error) {
      console.error('Error marking notifications as seen:', error);
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(showNotificationDefault);
    setCurrentNotifications([]);
    setGroupedNotifications([]);
    setSiaPersonalizationFailedNotification(null);
  };

  const handleActionClick = () => {
    if (currentNotifications.length > 0) {
      // If there are grouped notifications from the same influencer, use the campaign ID from the first one
      const campaignId = currentNotifications[0]?.content?.campaignId;
      window.location.href = `/dashboard/social/${campaignId}`;
    }
    handleCloseNotification();
  };

  const getNotificationAvatars = () => {
    // If all notifications are from the same influencer, just return one avatar
    if (groupedNotifications.length === 1) {
      return [groupedNotifications[0].content.profileImg || groupedNotifications[0].influencer.profile_pic];
    }

    // Otherwise return avatars for the first two different influencers
    return groupedNotifications.slice(0, 2).map((group) => group.content.profileImg || group.influencer.profile_pic);
  };

  const renderNotificationMessage = () => {
    // Case 1: All notifications are from the same influencer
    if (groupedNotifications.length === 1 && groupedNotifications[0].notifications.length > 1) {
      const { influencer, notifications } = groupedNotifications[0];
      const name = influencer.name || influencer.first_name;

      return (
        <p>
          <b className="font-medium">{name}</b> has mentioned you in {notifications.length} posts.
        </p>
      );
    }

    // Case 2: Single notification
    if (currentNotifications.length === 1) {
      const { content } = currentNotifications[0];
      return (
        <p>
          <b className="font-medium">{content.fullName}</b> has mentioned you in their Post.
        </p>
      );
    }

    // Case 3: Two different influencers
    else if (groupedNotifications.length === 2) {
      const first = groupedNotifications[0].influencer.first_name || groupedNotifications[0].influencer.name;
      const second = groupedNotifications[1].influencer.first_name || groupedNotifications[1].influencer.name;

      return (
        <p>
          <b className="font-medium">{first}</b> and <b className="font-medium">{second}</b> have mentioned you in their
          Posts.
        </p>
      );
    }

    // Case 4: More than two different influencers
    else {
      const first = groupedNotifications[0].influencer.first_name || groupedNotifications[0].influencer.name;
      const second = groupedNotifications[1].influencer.first_name || groupedNotifications[1].influencer.name;
      const othersCount = groupedNotifications.length - 2;

      return (
        <p>
          <b className="font-medium">{first}</b>, <b className="font-medium">{second}</b> and{' '}
          <b className="font-medium">{othersCount}</b> others have mentioned you in their Posts.
        </p>
      );
    }
  };

  return (
    <>
      <audio id="notification-sound" src="/audio/notification-bell.mp3" preload="auto" />
      {children}
      {showNotification.postTracking && (
        <CustomNotification
          open={showNotification.postTracking}
          onClose={handleCloseNotification}
          title="📣 New Mentions!"
          message={renderNotificationMessage()}
          actionText="Check it Out!"
          avatars={getNotificationAvatars()}
          autoHideDuration={6000} // 6 seconds
          instantClose={true}
          playSound={true}
          onActionClick={handleActionClick}
        />
      )}
      {showNotification.siaPersonalizationSuccess && (
        <CustomNotification
          open={showNotification.siaPersonalizationSuccess}
          onClose={handleCloseNotification}
          title="✨ SIA’s picked up your tone!"
          message="SIA has absorbed your guidelines & can now generate personalised responses to your emails."
          actionText="Try it Now"
          autoHideDuration={6000}
          instantClose={true}
          playSound={true}
          onActionClick={() => {
            window.location.href = '/dashboard/inbox';
            handleCloseNotification();
          }}
        />
      )}
      {showNotification.siaPersonalizationFailed && siaPersonalizationFailedNotification && (
        <CustomNotification
          open={showNotification.siaPersonalizationFailed}
          onClose={handleCloseNotification}
          title="‼️ SIA could not fully learn!"
          message={siaPersonalizationFailedNotification.content.message}
          actionText={
            siaPersonalizationFailedNotification.content.failureType ===
            SIA_PERSONALIZATION_FAILED_REASON.BRAND_GUIDELINES_FAILED
              ? 'Update Personalization Guidelines'
              : 'Update Website'
          }
          autoHideDuration={6000}
          instantClose={true}
          playSound={true}
          onActionClick={() => {
            window.open(
              `/dashboard/settings?tab=preferences&failure_type=${siaPersonalizationFailedNotification.content.failureType}`,
              '_blank'
            );
            handleCloseNotification();
          }}
        />
      )}
    </>
  );
}
